import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ArrowUpRight, X } from "lucide-react";
import { PRODUCTS_BUY_URL } from "@/lib/translations";

export default function ProductDetailDialog({ product, open, onOpenChange, t, language }) {
  if (!product) return null;
  const labels =
    language === "es"
      ? {
          benefits: "Beneficios",
          ingredients: "Ingredientes clave",
          usage: "Modo de uso",
          targets: "Ideal para",
          presentation: "Presentación",
          buy: "Comprar en Immunotec",
          aiHero: "Vida con Eliora",
          packshot: "Producto oficial",
        }
      : {
          benefits: "Benefits",
          ingredients: "Key ingredients",
          usage: "How to use",
          targets: "Ideal for",
          presentation: "Presentation",
          buy: "Buy on Immunotec",
          aiHero: "Life with Eliora",
          packshot: "Official product",
        };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="product-detail-dialog"
        className="max-w-4xl bg-[#f9f6f0] border-[#2c4a3b]/10 rounded-3xl p-0 overflow-hidden max-h-[92vh] overflow-y-auto"
      >
        <DialogTitle className="sr-only">{product.name[language]}</DialogTitle>
        <DialogDescription className="sr-only">{product.description[language]}</DialogDescription>

        <div className="grid lg:grid-cols-2">
          {/* IMAGES */}
          <div className="bg-[#f0ebe1] p-6 md:p-10 space-y-4">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-white flex items-center justify-center">
              <img
                src={product.officialImage || product.image}
                alt={`${product.name[language]} — pack`}
                className="w-full h-full object-contain p-4"
              />
            </div>
            {product.sachetImage && (
              <div className="aspect-square rounded-2xl overflow-hidden bg-white border border-[#2c4a3b]/8 flex items-center justify-center">
                <img
                  src={product.sachetImage}
                  alt={`${product.name[language]} — sobre`}
                  className="w-full h-full object-contain p-6"
                />
              </div>
            )}
          </div>

          {/* DETAILS */}
          <div className="p-6 md:p-10">
            <p className="text-overline">Immunotec</p>
            <h2
              className="font-serif font-light text-3xl md:text-4xl mt-3 text-[#1a2421] leading-tight"
              data-testid={`detail-name-${product.slug}`}
            >
              {product.name[language]}
            </h2>
            <p className="mt-3 text-[#5c6b61] leading-relaxed">
              {product.description[language]}
            </p>

            <Section title={labels.benefits}>
              <ul className="space-y-2">
                {product.benefits[language].map((b, i) => (
                  <li
                    key={i}
                    className="text-sm text-[#1a2421] flex gap-2 leading-snug"
                  >
                    <span className="text-[#bc6c25] mt-1 shrink-0">·</span>
                    {b}
                  </li>
                ))}
              </ul>
            </Section>

            {product.ingredients?.length > 0 && (
              <Section title={labels.ingredients}>
                <p className="text-sm text-[#5c6b61] leading-relaxed">
                  {product.ingredients.join(" · ")}
                </p>
              </Section>
            )}

            {product.usage && (
              <Section title={labels.usage}>
                <p className="text-sm text-[#1a2421] leading-relaxed">
                  {product.usage}
                </p>
              </Section>
            )}

            {product.targets?.length > 0 && (
              <Section title={labels.targets}>
                <div className="flex flex-wrap gap-2">
                  {product.targets.map((t, i) => (
                    <span
                      key={i}
                      className="bg-[#bc6c25]/10 text-[#9c581e] rounded-full px-3 py-1 text-xs font-medium"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </Section>
            )}

            {product.presentation && (
              <Section title={labels.presentation}>
                <p className="text-sm text-[#5c6b61]">{product.presentation}</p>
              </Section>
            )}

            <a
              href={PRODUCTS_BUY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`detail-buy-${product.slug}`}
              className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-[#bc6c25] hover:bg-[#9c581e] text-white px-6 py-4 text-sm font-semibold tracking-wide transition-all lift"
            >
              {labels.buy}
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-6 pt-5 border-t border-[#2c4a3b]/10">
      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#2c4a3b] mb-3">
        {title}
      </p>
      {children}
    </div>
  );
}
